import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Database, Download, RefreshCw, Play } from "lucide-react";

const fmtBytes = (n) => {
  const b = Number(n || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function Backups() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/staff/backups");
      setRows(res.data?.backups || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load backups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await api.post("/staff/backups/run");
      if (res.data?.ok) {
        toast.success(`Backup captured — ${res.data.file.split("/").pop()}`);
        load();
      } else {
        toast.error(res.data?.error || "Backup failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backup failed");
    } finally {
      setRunning(false);
    }
  };

  const downloadBackup = async (file) => {
    setDownloading(file);
    try {
      const token = localStorage.getItem("tbb_token");
      const res = await fetch(`${API}/staff/backups/download?file=${encodeURIComponent(file)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.split("/").pop();
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (err) {
      toast.error(err.message || "Download failed");
    } finally {
      setDownloading("");
    }
  };

  if (!isAdmin) {
    return (
      <section data-testid="backups-forbidden" className="p-8 border border-[hsl(var(--rule))] bg-[hsl(var(--bg))]">
        <p className="text-sm text-[hsl(var(--ink-soft))]">Database backups are admin-only.</p>
      </section>
    );
  }

  return (
    <section data-testid="backups-page" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="display text-3xl">Database backups</h1>
          <p className="text-sm text-[hsl(var(--ink-soft))]">
            A nightly snapshot of every collection runs at 02:30 IST. Backups are stored in Emergent Object Storage; download any snapshot as a single JSON file.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="backups-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 border border-[hsl(var(--rule))] text-sm hover:bg-[hsl(var(--bg-2))]"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            data-testid="backups-run-now"
            onClick={runNow}
            disabled={running}
            className="inline-flex items-center gap-2 px-3 py-2 bg-[hsl(var(--forest))] text-[hsl(var(--bg))] text-sm hover:bg-[hsl(var(--forest-2))] disabled:opacity-50"
          >
            <Play size={14} /> {running ? "Running…" : "Run backup now"}
          </button>
        </div>
      </header>

      <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg))]">
        <div className="p-4 border-b border-[hsl(var(--rule))] flex items-center gap-2 text-sm text-[hsl(var(--ink-soft))]">
          <Database size={14} /> {loading ? "Loading…" : `${rows.length} backup snapshot${rows.length === 1 ? "" : "s"}`}
        </div>
        {!loading && rows.length === 0 ? (
          <div className="p-8 text-sm text-[hsl(var(--ink-soft))]" data-testid="backups-empty">
            No backups yet. Click <span className="font-semibold">Run backup now</span> to create the first snapshot, or wait for the nightly 02:30 IST run.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--bg-2))] text-left text-xs uppercase tracking-wide text-[hsl(var(--ink-soft))] sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
                <tr>
                  <th className="px-4 py-2">File</th>
                  <th className="px-4 py-2">Captured at</th>
                  <th className="px-4 py-2 text-right">Collections</th>
                  <th className="px-4 py-2 text-right">Documents</th>
                  <th className="px-4 py-2 text-right">Size</th>
                  <th className="px-4 py-2 text-right">Download</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.id} data-testid={`backup-row-${b.id}`} className="border-t border-[hsl(var(--rule))]">
                    <td className="px-4 py-2 font-mono text-xs">{b.file.split("/").pop()}</td>
                    <td className="px-4 py-2">{fmtWhen(b.created_at)}</td>
                    <td className="px-4 py-2 text-right">{b.collections}</td>
                    <td className="px-4 py-2 text-right">{Number(b.total_docs || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2 text-right">{fmtBytes(b.size_bytes)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        data-testid={`backup-download-${b.id}`}
                        onClick={() => downloadBackup(b.file)}
                        disabled={downloading === b.file}
                        className="inline-flex items-center gap-1 text-[hsl(var(--forest))] hover:underline disabled:opacity-50"
                      >
                        <Download size={14} /> {downloading === b.file ? "…" : "Download"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
